import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { Autocomplete,Box,Checkbox,Chip,MenuItem,Stack,TextField,Tooltip,Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { OptionType } from "../../types/helpers/types";


type MultiSelectAutocompleteProps = {
  options: OptionType[];
  selectedValues: { label: string, value: string | number }[];
  onChange: (newValues: { label: string, value: string | number }[]) => void;
  loading: boolean,
  textLabel: string,
  onOpen?: () => void;
  onListboxScroll?: (event: React.UIEvent<HTMLUListElement>) => void;
  error?: boolean;
  loaded?: boolean;
  emptyText?: string;
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AutocompleteSemApi: React.FC<MultiSelectAutocompleteProps> = ({
  selectedValues,
  options,
  onChange,
  loading,
  textLabel,
  onOpen,
  onListboxScroll,
  error = false,
  loaded = false,
  emptyText,
}) => {

  const { t } = useTranslation('translation');

  const allOptions = options.length ? [
    { label: 'Selecionar todos', value: 'select_all', isSelectAll: true },
    ...options.sort((a, b) => {
      const aSelected = selectedValues.some((ele) => ele.value === a.value);
      const bSelected = selectedValues.some((ele) => ele.value === b.value);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.label?.localeCompare(b.label);
    }),
  ] : [];

  const truncate = (text: string) => text?.length > 15 ? `${text?.slice(0, 15)}...` : text;

  function checkValuesLenght(value: { value: string, label: string }[]) {
    if (value.length >= 30) {
      return true;
    } else return false;
  }

  const MAX_SELECTION = 30;

  return (
    <Stack width='100%' direction='column' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
      <Autocomplete
        fullWidth
        multiple
        disableCloseOnSelect
        disableClearable
        loading={loading}
        loadingText="Buscando dados..."
        onOpen={onOpen}
        ListboxProps={{
          onScroll: onListboxScroll,
        }}
        noOptionsText={error ? 'Erro ao carregar opções. Abra novamente para tentar.' : (emptyText ?? 'Nenhuma opção encontrada')}
        options={allOptions.filter((element, index, array) => {
          const firstIndex = array.findIndex(item => item.value === element.value);
          return index === firstIndex;
        }) ?? []}
        value={options.filter((opt) => selectedValues.some((ele) => ele.value === opt.value))}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(event, newValue, reason, details) => {
          if (!details?.option) return;

          const { label, value, isSelectAll } = details.option;

          // 👉 Selecionar todos
          if (isSelectAll) {
            const allSelected =
              options.length > 0 &&
              options.every((opt) => selectedValues.some((ele) => ele.value === opt.value));

            if (allSelected) {
              onChange([]);
              return;
            }

            // seleciona apenas até o limite
            const limitedOptions = options.slice(0, MAX_SELECTION);
            onChange(limitedOptions);
            return;
          }

          const alreadySelected = selectedValues.some((ele) => ele.value === value);

          // 👉 Tentando adicionar mais que o limite
          if (!alreadySelected && selectedValues.length >= MAX_SELECTION) {
            return; // bloqueia a seleção
          }

          const updatedList = alreadySelected
            ? selectedValues.filter((v) => v.value !== value)
            : [...selectedValues, { label, value }];

          onChange(updatedList);
        }}
        getOptionDisabled={(option) =>
          !option.isSelectAll &&
          selectedValues.length >= MAX_SELECTION &&
          !selectedValues.some((v) => v.value === option.value)
        }
        renderOption={(props, option, { selected }) => {
          if ((option as OptionType).isSelectAll) {
            const allSelected =
              options.length > 0 &&
              options.every((opt) => selectedValues.some((ele) => ele.value === opt.value));

            return (
              <MenuItem {...props} key="select_all">
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  checked={allSelected}
                  disabled={!allSelected && selectedValues.length >= MAX_SELECTION}
                />
                <b>{allSelected
                  ? t('Reports.autocomplete.unselectAll')
                  : allOptions.length > 30
                    ? t('Reports.autocomplete.selectFirstThirty')
                    : t('Reports.autocomplete.selectAll')}</b>
              </MenuItem>
            );
          }

          return (
            <MenuItem {...props} key={option.value}>
              <Checkbox
                disabled={loading}
                icon={icon}
                checkedIcon={checkedIcon}
                checked={selected}
                style={{ marginRight: 8 }}
              />
              <Tooltip title={option.label}>
                <span>{option.label}</span>
              </Tooltip>
            </MenuItem>
          );
        }}
        renderTags={(value, getTagProps) => {
          const displayedTags = value.slice(0, 2);
          return (
            <>
              {displayedTags.filter((element, index, array) => {
                const firstIndex = array.findIndex(item => item.value === element.value);
                return index === firstIndex;
              }).map((option, index) => {
                const tagProps = getTagProps({ index });
                return (
                  <Chip
                    {...tagProps}
                    key={option.value}
                    label={truncate(option.label)}
                    onDelete={tagProps.onDelete}
                  />
                );
              })}
              {value.length > 2 && (
                <Chip
                  label={`+${value.length - 2}`}
                  sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}
                />
              )}
            </>
          );
        }}
        renderInput={(params) => (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField {...params} label={textLabel} />
            {selectedValues.length > 0 && (
              <ClearAllIcon
                onClick={() => onChange([])}
                sx={{
                  position: 'absolute',
                  right: 40,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'gray',
                  cursor: 'pointer',
                  '&:hover': { color: 'black' },
                }}
                titleAccess={t('Reports.autocomplete.clearSelection')}
              />
            )}
          </Box>
        )}
      />
      {error && (
        <Typography color="error" variant="caption">
          Erro ao carregar opções. Abra o seletor novamente para tentar.
        </Typography>
      )}
      {loading && (
        <Typography color="textSecondary" variant="caption">
          Buscando dados...
        </Typography>
      )}
      {loaded && !loading && !error && options.length === 0 && (
        <Typography color="textSecondary" variant="caption">
          Nenhuma opção encontrada.
        </Typography>
      )}
      {checkValuesLenght(selectedValues as { label: string, value: string }[])
        && <Typography color='textDisabled' fontWeight={600}>{t('checklists.Modal.filterMaxValueReached')}</Typography>}
    </Stack>
  );
};

export default AutocompleteSemApi;
